import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button } from "reactstrap";

import { userHasAnyRole } from "../../../services/authService";
import { getUnits } from "../../../services/unitService";
import { getUnitSchedules } from "../../../services/unitScheduleService";

type Props = {
  todayIso: string;
};

const greetingKeyForHour = (hour: number): string => {
  if (hour < 12) {
    return "studio.dashboard.home.greetingMorning";
  }
  if (hour < 18) {
    return "studio.dashboard.home.greetingAfternoon";
  }
  return "studio.dashboard.home.greetingEvening";
};

/** Print-style clock: 09h / 19h30 */
const formatClockLabel = (value: string): string => {
  const match = value.match(/^(\d{1,2}):(\d{2})/);
  if (!match) {
    return value.slice(0, 5).replace(":", "h");
  }
  const hour = match[1].padStart(2, "0");
  const minute = match[2];
  if (minute === "00") {
    return `${hour}h`;
  }
  return `${hour}h${minute}`;
};

const DashboardHomeHeader = ({ todayIso }: Props) => {
  const { t, i18n } = useTranslation();
  const canCreate = userHasAnyRole(["owner", "manager", "receptionist", "sdr"]);
  const [hoursLabel, setHoursLabel] = useState<string | null>(null);

  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    return t(greetingKeyForHour(hour));
  }, [t]);

  const dateLabel = useMemo(() => {
    const [year, month, day] = todayIso.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString(i18n.language, {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }, [i18n.language, todayIso]);

  const weekday = useMemo(() => {
    const [year, month, day] = todayIso.split("-").map(Number);
    return new Date(year, month - 1, day).getDay();
  }, [todayIso]);

  useEffect(() => {
    let cancelled = false;

    const loadHours = async () => {
      try {
        const unitsResponse = await getUnits({ page: 1, per_page: 1 });
        const unit = unitsResponse.data?.[0];
        if (!unit) {
          if (!cancelled) {
            setHoursLabel(null);
          }
          return;
        }
        const schedulesResponse = await getUnitSchedules(unit.id);
        const todaySlots = (schedulesResponse.data ?? []).filter(
          (slot) => slot.day_of_week === weekday
        );
        if (todaySlots.length === 0) {
          if (!cancelled) {
            setHoursLabel(t("studio.dashboard.home.hoursClosed"));
          }
          return;
        }
        const starts = todaySlots.map((slot) => slot.start_time).sort();
        const ends = todaySlots.map((slot) => slot.end_time).sort();
        if (!cancelled) {
          setHoursLabel(
            t("studio.dashboard.home.hoursRange", {
              start: formatClockLabel(starts[0]),
              end: formatClockLabel(ends[ends.length - 1]),
            })
          );
        }
      } catch {
        if (!cancelled) {
          setHoursLabel(null);
        }
      }
    };

    void loadHours();
    return () => {
      cancelled = true;
    };
  }, [t, weekday]);

  return (
    <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-4">
      <div>
        <h2 className="mb-1 fw-semibold lh-sm" style={{ letterSpacing: "-0.02em" }}>
          {greeting}
        </h2>
        <p className="text-muted mb-0 fs-6 text-capitalize">
          {dateLabel}
          {hoursLabel ? ` · ${hoursLabel}` : ""}
        </p>
      </div>
      {canCreate ? (
        <Button tag={Link} to="/agenda?action=create" color="success" className="px-3">
          {t("studio.dashboard.home.newAppointment")}
        </Button>
      ) : null}
    </div>
  );
};

export default DashboardHomeHeader;
